/**
 * Firebase Emulator Configuration
 *
 * Centralized configuration for Firebase emulator connections.
 * Update these settings based on your development environment.
 */

import { Capacitor } from "@capacitor/core";

const isNativePlatform = Capacitor.isNativePlatform();
const isAndroid = Capacitor.getPlatform() === "android";
const env = (import.meta as any).env || {};

/**
 * Enable/disable emulator mode
 * Enabled only when USE_FIREBASE_EMULATOR is explicitly set to "true".
 * If unset (default in production), production Firebase services are used.
 */
export const USE_EMULATOR = process.env.USE_FIREBASE_EMULATOR === "true";

console.log(`Emulator mode: ${USE_EMULATOR ? "ENABLED" : "DISABLED"}`);

/**
 * Emulator host configuration
 * - For web/browser: use "localhost"
 * - For native apps on physical devices: use your computer's local IP address
 *
 * To find your Mac's local IP: ipconfig getifaddr en0
 * Example: "192.168.1.100"
 */
const normalizeHost = (host: string | undefined): string => {
  if (!host || host === "") return "localhost";
  if (host === "0.0.0.0") return "127.0.0.1";
  if (host === "::1") return "127.0.0.1";
  return host;
};

export const EMULATOR_HOST = Capacitor.isNativePlatform()
  ? normalizeHost(
    env.VITE_EMULATOR_HOST_NATIVE ||
      // Android emulator reaches host machine via 10.0.2.2
      (isAndroid ? "10.0.2.2" : "127.0.0.1"),
  )
  : normalizeHost(window.location.hostname);

/**
 * Emulator port configuration
 * Must match the ports defined in firebase.json
 */
export const EMULATOR_PORTS = {
  auth: 9099,
  firestore: 9005,
  storage: 9006,
  functions: 9003,
  ui: 4000,
} as const;

/**
 * Get emulator URL for a specific service
 */
export function getEmulatorUrl(service: keyof typeof EMULATOR_PORTS): string {
  return `http://${EMULATOR_HOST}:${EMULATOR_PORTS[service]}`;
}